/**
 * Layout tree helpers — pure functions over the recursive binary split tree.
 * A leaf hosts one editor group; a split node divides the area into two
 * children with percentage sizes (clamped 10–90 by the divider drag).
 */

import type {
  DocGroup,
  DocLayoutNode,
  SplitDirection,
} from '../types';

let groupCounter = 0;

/** Fresh group id — timestamp + counter so two calls in one ms never clash */
export const createGroupId = (namespace: string): string => {
  groupCounter += 1;
  return `${namespace}-group-${Date.now()}-${groupCounter}`;
};

/** Create an empty group record */
export const createGroup = (id: string): DocGroup => ({
  id,
  tabIds: [],
  activeTabId: null,
});

/** All leaf group ids of the tree, in layout order (left/top first) */
export const leafGroupIds = (node: DocLayoutNode): string[] => {
  if (node.type === 'leaf') return [node.groupId];
  return [...leafGroupIds(node.children[0]), ...leafGroupIds(node.children[1])];
};

/** First leaf group of the tree — the default docking target */
export const firstLeafGroup = (node: DocLayoutNode): string => {
  const first = leafGroupIds(node)[0];
  return first;
};

/** The id of the leaf group hosting `groupId`'s sibling in a split whose
 * direction matches, plus the node itself. Returns null when the tree has
 * no split node. */
export const findLeafNode = (
  node: DocLayoutNode,
  groupId: string,
): Extract<DocLayoutNode, { type: 'leaf' }> | null => {
  if (node.type === 'leaf') return node.groupId === groupId ? node : null;
  return findLeafNode(node.children[0], groupId) ?? findLeafNode(node.children[1], groupId);
};

/** Parent split of a leaf (or null when the leaf is the root) */
export const findParentOfLeaf = (
  node: DocLayoutNode,
  groupId: string,
): Extract<DocLayoutNode, { type: 'split' }> | null => {
  if (node.type === 'leaf') return null;
  const [a, b] = node.children;
  if (
    (a.type === 'leaf' && a.groupId === groupId) ||
    (b.type === 'leaf' && b.groupId === groupId)
  ) {
    return node;
  }
  return findParentOfLeaf(a, groupId) ?? findParentOfLeaf(b, groupId);
};

/** Replace the leaf hosting `groupId` with `replacement` */
export const replaceLeaf = (
  node: DocLayoutNode,
  groupId: string,
  replacement: DocLayoutNode,
): DocLayoutNode => {
  if (node.type === 'leaf') {
    return node.groupId === groupId ? replacement : node;
  }
  return {
    ...node,
    children: [
      replaceLeaf(node.children[0], groupId, replacement),
      replaceLeaf(node.children[1], groupId, replacement),
    ] as [DocLayoutNode, DocLayoutNode],
  };
};

/** Update the sizes of one split node (matched by stable node id) */
export const updateSplitSizes = (
  node: DocLayoutNode,
  target: Extract<DocLayoutNode, { type: 'split' }>,
  sizes: [number, number],
): DocLayoutNode => {
  if (node.type === 'leaf') return node;
  if (node.id === target.id) return { ...node, sizes };
  return {
    ...node,
    children: [
      updateSplitSizes(node.children[0], target, sizes),
      updateSplitSizes(node.children[1], target, sizes),
    ] as [DocLayoutNode, DocLayoutNode],
  };
};

/** Groups referenced by the tree */
const collectGroupIds = (node: DocLayoutNode, into: Set<string>): void => {
  if (node.type === 'leaf') {
    into.add(node.groupId);
    return;
  }
  collectGroupIds(node.children[0], into);
  collectGroupIds(node.children[1], into);
};

const isEmptyLeaf = (node: DocLayoutNode, groups: Record<string, DocGroup>): boolean => {
  if (node.type !== 'leaf') return false;
  const group = groups[node.groupId];
  return !group || group.tabIds.length === 0;
};

/** Prune empty leaves; a fully empty root split collapses to its second
 * leaf's group (pik's collapseEmptyPanels semantics, generalized). Returns
 * the next tree plus the set of group ids that must be deleted. */
export const pruneEmptyLeaves = (
  node: DocLayoutNode,
  groups: Record<string, DocGroup>,
): { layout: DocLayoutNode; removedGroupIds: string[] } => {
  const removed: string[] = [];
  const prune = (current: DocLayoutNode): DocLayoutNode => {
    if (current.type === 'leaf') return current;
    let a = prune(current.children[0]);
    let b = prune(current.children[1]);
    if (isEmptyLeaf(a, groups) && isEmptyLeaf(b, groups)) {
      // Keep the SECOND leaf's group (pik behavior); drop the first
      if (a.type === 'leaf') removed.push(a.groupId);
      return b;
    }
    if (isEmptyLeaf(a, groups)) {
      if (a.type === 'leaf') removed.push(a.groupId);
      return b;
    }
    if (isEmptyLeaf(b, groups)) {
      if (b.type === 'leaf') removed.push(b.groupId);
      return a;
    }
    if (a !== current.children[0] || b !== current.children[1]) {
      return { ...current, children: [a, b] as [DocLayoutNode, DocLayoutNode] };
    }
    return current;
  };
  const layout = prune(node);
  return { layout, removedGroupIds: removed };
};

/** Group ids present in `groups` but not referenced by the tree */
export const orphanGroupIds = (
  node: DocLayoutNode,
  groups: Record<string, DocGroup>,
): string[] => {
  const referenced = new Set<string>();
  collectGroupIds(node, referenced);
  return Object.keys(groups).filter((id) => !referenced.has(id));
};

/** Split a leaf in the given direction; the moved tab lands ALONE in the new
 * group (pik's splitPanelWithDocument semantics). Returns the next tree and
 * the new group id. The new split node gets `nodeId` so the divider drag can
 * address it stably. */
export const splitLeafWithGroup = (
  layout: DocLayoutNode,
  targetGroupId: string,
  direction: SplitDirection,
  position: 'before' | 'after',
  newGroupId: string,
  nodeId: string,
): DocLayoutNode => {
  const newLeaf: DocLayoutNode = { type: 'leaf', groupId: newGroupId };
  const existingLeaf: DocLayoutNode = { type: 'leaf', groupId: targetGroupId };
  const replacement: DocLayoutNode = {
    type: 'split',
    id: nodeId,
    direction,
    sizes: [50, 50],
    children:
      position === 'before'
        ? [newLeaf, existingLeaf]
        : [existingLeaf, newLeaf],
  };
  return replaceLeaf(layout, targetGroupId, replacement);
};
